/**
 * AudioManager - Web Audio API manager for game sounds
 * Handles loading, playing, and volume control for all game audio.
 *
 * Sound effects (procedurally generated, inspired by OpenGameArt.org):
 *   - launch: Player fires a counter-missile (rocket ignition whoosh)
 *   - explosion_small: Small explosion (intercept hit)
 *   - explosion_medium: Medium explosion (player missile detonation)
 *   - explosion_large: Large explosion (battery/satellite destruction)
 *   - city_destroy: City atomic detonation (nuclear explosion with mushroom cloud rumble)
 *   - wave_complete: Wave cleared fanfare (triumphant arpeggio)
 *   - empty_click: No ammo click (mechanical denial)
 *   - ambient: Background atmosphere during gameplay (sci-fi tension)
 *   - mirv_split: MIRV warhead separation (cracking dispersal)
 *   - missile_incoming: Enemy missile warning (descending whistle)
 *   - score_tick: Score counting blip (tally animation)
 *   - game_over: Game over sequence (dramatic descending minor)
 */

export class AudioManager {
  constructor() {
    this.context = null;
    this.masterVolume = 1.0;
    this.sounds = {};
    this.initialized = false;

    // Ambient audio state
    this._ambientSource = null;
    this._ambientGain = null;

    // Load saved volume
    this._loadVolume();
  }

  /**
   * Initialize the Web Audio API context.
   * Must be called after a user interaction (browser autoplay policy).
   */
  init() {
    if (this.initialized) return;

    try {
      this.context = new (window.AudioContext || window.webkitAudioContext)();
      this.masterGain = this.context.createGain();
      this.masterGain.gain.value = this.masterVolume;
      this.masterGain.connect(this.context.destination);
      this.initialized = true;
      console.log('Audio system initialized.');
    } catch (e) {
      console.warn('Web Audio API not available:', e);
    }
  }

  /**
   * Set master volume (0.0 to 1.0).
   */
  setVolume(volume) {
    this.masterVolume = Math.max(0, Math.min(1, volume));
    if (this.masterGain) {
      this.masterGain.gain.value = this.masterVolume;
    }
    this._saveVolume();
  }

  /**
   * Get current master volume.
   */
  getVolume() {
    return this.masterVolume;
  }

  /**
   * Resume audio context if suspended (required after user interaction).
   */
  resume() {
    if (this.context && this.context.state === 'suspended') {
      this.context.resume().then(() => {
        console.log('Audio context resumed.');
      }).catch(e => {
        console.warn('Audio context resume failed:', e);
      });
    }
  }

  /**
   * Play a sound effect by name.
   * @param {string} name - Sound name (e.g., 'launch', 'explosion_small')
   */
  play(name) {
    if (!this.initialized || !this.context || !this.masterGain) return;

    const buffer = this.sounds[name];
    if (!buffer) return;

    // Resume context if needed (browser autoplay policy)
    if (this.context.state === 'suspended') {
      this.context.resume();
    }

    try {
      const source = this.context.createBufferSource();
      source.buffer = buffer;
      source.connect(this.masterGain);
      source.start(0);
    } catch (e) {
      console.warn(`Error playing sound "${name}":`, e);
    }
  }

  /**
   * Start looping ambient background audio during gameplay.
   * Creates a low-volume looping sound from the 'ambient' buffer.
   */
  startAmbient() {
    if (!this.initialized || !this.context || !this.masterGain) return;
    if (this._ambientSource) return; // Already playing

    const buffer = this.sounds['ambient'];
    if (!buffer) return;

    // Resume context if needed
    if (this.context.state === 'suspended') {
      this.context.resume();
    }

    try {
      // Create a gain node for ambient volume control (lower than SFX)
      this._ambientGain = this.context.createGain();
      this._ambientGain.gain.value = 0.0;
      this._ambientGain.connect(this.masterGain);

      const source = this.context.createBufferSource();
      source.buffer = buffer;
      source.loop = true;
      source.connect(this._ambientGain);
      source.start(0);
      this._ambientSource = source;

      // Fade in over 1 second
      this._ambientGain.gain.linearRampToValueAtTime(0.35, this.context.currentTime + 1.0);
    } catch (e) {
      console.warn('Error starting ambient audio:', e);
    }
  }

  /**
   * Stop the ambient background audio with a fade-out.
   */
  stopAmbient() {
    if (!this._ambientSource || !this._ambientGain || !this.context) return;

    try {
      // Fade out over 0.5 seconds then stop
      const now = this.context.currentTime;
      this._ambientGain.gain.cancelScheduledValues(now);
      this._ambientGain.gain.setValueAtTime(this._ambientGain.gain.value, now);
      this._ambientGain.gain.linearRampToValueAtTime(0.0, now + 0.5);

      const source = this._ambientSource;
      setTimeout(() => {
        try { source.stop(); } catch (e) { /* already stopped */ }
      }, 600);

      this._ambientSource = null;
      this._ambientGain = null;
    } catch (e) {
      console.warn('Error stopping ambient audio:', e);
    }
  }

  /**
   * Check if audio system is ready.
   */
  isReady() {
    return this.initialized && this.context !== null;
  }

  _loadVolume() {
    try {
      const saved = localStorage.getItem('missileCommand_volume');
      if (saved !== null) {
        this.masterVolume = parseFloat(saved);
      }
    } catch (e) {
      // localStorage not available
    }
  }

  _saveVolume() {
    try {
      localStorage.setItem('missileCommand_volume', this.masterVolume.toString());
    } catch (e) {
      // localStorage not available
    }
  }
}
